import { del, get, patch, post, put } from './api';
import type {
  LabelCount,
  ProposalComparison,
  Thread,
  ThreadKind,
  ThreadList,
  Timeline,
  TimelineEntry,
} from '../shared/types/api';
import { SECTION } from '../shared/utils/conversations';

const wiki = (repoId: string) => `/repositories/${encodeURIComponent(repoId)}`;
const thread = (repoId: string, number: number) => `${wiki(repoId)}/threads/${number}`;

export interface ThreadFilters {
  state?: string;
  q?: string;
  label?: string;
  category?: string;
  pagePath?: string;
  sort?: string;
  page?: number;
  perPage?: number;
}

function query(values: Record<string, string | number | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}

export const listThreads = (repoId: string, kind: ThreadKind, filters: ThreadFilters = {}) =>
  get<ThreadList>(`${wiki(repoId)}/${SECTION[kind]}${query({ ...filters })}`);

export interface NewThread {
  title: string;
  body?: string;
  pagePath?: string;
  category?: string;
  sourceRepositoryId?: string;
}
export const createThread = (repoId: string, kind: ThreadKind, input: NewThread) =>
  post<Thread>(`${wiki(repoId)}/${SECTION[kind]}`, input);

export const getThread = (repoId: string, number: number) => get<Thread>(thread(repoId, number));

export interface ThreadChanges {
  title?: string;
  body?: string;
  state?: 'open' | 'closed';
  labels?: string[];
  pagePath?: string;
  category?: string;
}
export const updateThread = (repoId: string, number: number, changes: ThreadChanges) =>
  patch<Thread>(thread(repoId, number), changes);

export const getTimeline = (repoId: string, number: number, page = 1) =>
  get<Timeline>(`${thread(repoId, number)}/entries${query({ page })}`);

export const addComment = (repoId: string, number: number, body: string, path?: string) =>
  post<TimelineEntry>(`${thread(repoId, number)}/comments`, path ? { body, path } : { body });

export const editComment = (repoId: string, number: number, entryId: string, body: string) =>
  patch<TimelineEntry>(`${thread(repoId, number)}/comments/${encodeURIComponent(entryId)}`, { body });

export const deleteComment = (repoId: string, number: number, entryId: string) =>
  del<{ ok: true }>(`${thread(repoId, number)}/comments/${encodeURIComponent(entryId)}`);

export const setAnswer = (repoId: string, number: number, entryId: string | null) =>
  put<Thread>(`${thread(repoId, number)}/answer`, { entryId });

export const listLabels = (repoId: string) => get<{ items: LabelCount[] }>(`${wiki(repoId)}/labels`);

export const compareProposal = (repoId: string, number: number) =>
  get<ProposalComparison>(`${wiki(repoId)}/proposals/${number}/compare`);

export const reviewProposal = (
  repoId: string,
  number: number,
  state: 'comment' | 'approved' | 'changes_requested',
  body: string,
) => post<TimelineEntry>(`${wiki(repoId)}/proposals/${number}/reviews`, { state, body });

export const refreshProposal = (repoId: string, number: number) =>
  post<Thread>(`${wiki(repoId)}/proposals/${number}/refresh`, {});

export const mergeProposal = (repoId: string, number: number, message?: string) =>
  post<Thread>(`${wiki(repoId)}/proposals/${number}/merge`, message ? { message } : {});
